<template>
  <div class="content container">
    <h1 class="heading">Sandford dashboard</h1>

    <h2 class="subheading">Download CSV</h2>
    <p class="hint">Set the date ranges and granularity you wish to export.</p>

    <div class="flex flex-row filters">
      <div class="flex-1 flex flex-col">
        <label>Date range</label>

        <HotelDatePicker
          @check-in-changed="setStartDate"
          @check-out-changed="setEndDate"
          :startDate="startingDate"
          :endDate="endingDate"
          :i18n="enGB"
        />
      </div>

      <div class="flex-1 flex flex-col" style="max-width: 170px">
        <label>Granularity</label>
        <multiselect
          :show-labels="false"
          :preselectFirst="true"
          :allow-empty="false"
          v-model="timing"
          :options="timings"
        ></multiselect>
      </div>

      <div class="flex flex-col flex-1" style="max-width: 170px">
        <a :href="downloadUrl" class="block btn btn--block btn--l btn--main text-center mt-auto"
          >Download CSV</a
        >
      </div>
    </div>

    <hr />

    <h2 class="subheading">Live data</h2>

    <p class="hint">
      Data from <strong>{{ recordingFormattedDate }}</strong
      >. Should update every minute.
    </p>

    <div v-for="(value, key) in latestValues" :key="key">
      <strong>{{ key }}:</strong> {{ value }} {{ unit(key) }}
    </div>

    <hr />

    <h2 class="subheading">Historic readings</h2>
    <p class="hint">Data for the last 48 hours</p>
    <line-chart :height="300" :chart-data="datacollection" :options="chartOptions"></line-chart>

    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
// import 'vue-hotel-datepicker/dist/vue-hotel-datepicker.min.css';
import axios from '@/axios';
import { format } from 'date-fns';
import LineChart from '@/components/LineChart.js';
import HotelDatePicker from 'vue-hotel-datepicker';
// @ is an alias to /src
// import HelloWorld from '@/components/HelloWorld.vue';

export default {
  name: 'dashboard',
  components: {
    HotelDatePicker,
    LineChart
  },
  data() {
    return {
      interval: null,
      historicData: null,
      datacollection: null,
      timing: 'Minute by minute',
      timings: [
        'Minute by minute',
        '5 minutes',
        '10 minutes',
        '15 minutes',
        '30 minutes',
        '60 minutes'
      ],
      endDate: null,
      startDate: null,
      error: null,
      latestValues: {},
      maxValues: {},
      enGB: {
        night: 'day',
        nights: 'days',
        'check-in': 'Start Date',
        'check-out': 'End date',
        'day-names': ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        'month-names': [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ]
      },
      chartOptions: {
        responsive: true,
        title: {
          display: false
        },
        tooltips: {
          mode: 'index',
          intersect: false
        },
        hover: {
          mode: 'nearest',
          intersect: true
        },
        scales: {
          xAxes: [
            {
              display: true,
              scaleLabel: {
                display: true,
                labelString: 'day and time'
              }
            }
          ],
          yAxes: [
            {
              display: true,
              scaleLabel: {
                display: false
              }
            }
          ]
        }
      },
      units: {
        Voltage_U: {
          name: 'Voltage_U',
          unit: 'V',
          backgroundColor: '#f1f5f8'
        },
        Voltage_V: {
          name: 'Voltage_V',
          unit: 'V',
          backgroundColor: '#f9acaa'
        },
        Voltage_W: {
          name: 'Voltage_W',
          unit: 'V',
          backgroundColor: '#fcd9b6'
        },
        Voltage_UV: {
          name: 'Voltage_UV',
          unit: 'V',
          backgroundColor: '#fff9c2'
        },
        Voltage_VW: {
          name: 'Voltage_VW',
          unit: 'V',
          backgroundColor: '#a2f5bf'
        },
        Voltage_UW: {
          name: 'Voltage_UW',
          unit: 'V',
          backgroundColor: '#a0f0ed'
        },
        Voltage_SYS: {
          name: 'Voltage_SYS',
          unit: 'V',
          backgroundColor: '#bcdefa'
        },
        Current_U: {
          name: 'Current_U',
          unit: 'A',
          backgroundColor: '#b2b7ff'
        },
        Current_V: {
          name: 'Current_V',
          unit: 'A',
          backgroundColor: '#d6bbfc'
        },
        Current_W: {
          name: 'Current_W',
          unit: 'A',
          backgroundColor: '#ffbbca'
        },
        Current_SYS: {
          name: 'Current_SYS',
          unit: 'A',
          backgroundColor: '#f1f5f8'
        },
        Power_Factor_SYS: {
          name: 'Power_Factor_SYS',
          unit: '%',
          backgroundColor: '#f9acaa'
        },
        Active_Power_SYS: {
          name: 'Active_Power_SYS',
          unit: 'kW',
          backgroundColor: '#fcd9b6'
        },
        Apparet_Power_SYS: {
          name: 'Apparet_Power_SYS',
          unit: 'kVA',
          backgroundColor: '#fff9c2'
        },
        Reactive_Power_SYS: {
          name: 'Reactive_Power_SYS',
          unit: 'kVAr',
          backgroundColor: '#a2f5bf'
        },
        Active_Energy_Exported: {
          name: 'Active_Energy_Exported',
          unit: 'kWh',
          backgroundColor: '#a0f0ed'
        }
      }
    };
  },
  mounted() {
    this.fillData();
  },
  created() {
    // fetch the data when the view is created and the data is
    // already being observed
    this.fetchData();
  },
  destroyed() {
    try {
      clearInterval(this.interval);
    } catch (err) {
      this.interval = null;
    }
  },
  watch: {
    // call again the method if the route changes
    $route: 'fetchData'
  },
  computed: {
    downloadUrl() {
      const url = process.env.NODE_ENV === 'development' ? 'http://localhost:9000' : '';
      const path = '/api/v2/sandfords/csv';
      const token = this.$store.getters.token;
      let query = '';

      if (this.startDate) {
        query += `&start=${Math.round(this.startDate.getTime() / 1000)}`;
      }

      if (this.endDate) {
        query += `&end=${Math.round(this.endDate.getTime() / 1000)}`;
      }

      if (this.timing) {
        query += `&timing=${this.timing}`;
      }

      return `${url}${path}?access_token=${token}${query}`;
    },
    startingDate() {
      return new Date(2019, 0, 30);
    },
    endingDate() {
      return new Date();
    },
    recordingFormattedDate() {
      return this.formattedDate(this.latestValues.datetime);
    }
  },
  methods: {
    // https://router.vuejs.org/guide/advanced/data-fetching.html#fetching-before-navigation
    async fetchData() {
      this.installations = [];
      this.loading = true;

      await this.fetchMaxValues();
      await this.fetchLatestValues();

      const refreshInterval = 60 * 1000;
      this.interval = setInterval(() => {
        this.fetchLatestValues();
      }, refreshInterval);

      this.loaded = true;
    },
    async fetchMaxValues() {
      const API_call = await axios.get('/api/v2/sandfords/max').catch(() => false);

      if (!API_call) {
        // TODO: show error
        return;
      }

      this.maxValues = API_call.data;
    },
    async fetchLatestValues() {
      const API_call = await axios.get('/api/v2/sandfords/latest').catch(() => false);

      if (!API_call) {
        // TODO: show error
        return;
      }

      this.latestValues = API_call.data;
    },
    unit(name) {
      return this.units[name] ? this.units[name].unit : '';
    },
    setStartDate(daterange) {
      this.startDate = daterange;
    },
    setEndDate(daterange) {
      this.endDate = daterange;
    },
    async fillData() {
      const historicData = await axios.get('/api/v2/sandfords/historic').catch(() => false);

      if (historicData) {
        this.historicData = historicData.data;
        this.updateData();
      }
    },
    hexToRgbA(hex, opacity = 1) {
      var c;
      if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('');
        if (c.length == 3) {
          c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = '0x' + c.join('');
        return (
          'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ', ' + opacity + ')'
        );
      }

      throw new Error('Bad Hex');
    },
    updateData() {
      if (!this.historicData) {
        return;
      }

      const dates = this.historicData.map(point => this.formattedDate(point.datetime, 'do@HH:mm'));

      const datasets = Object.keys(this.units)
        .filter(key => key !== 'Active_Energy_Exported')
        .map(key => {
          const type = this.units[key];
          const data = this.historicData.map(point => point[key]);

          const gradient = this.$refs.canvas.getContext('2d').createLinearGradient(0, 0, 200, 0);
          gradient.addColorStop(0, this.hexToRgbA(type.backgroundColor, 0.75));
          gradient.addColorStop(0.5, this.hexToRgbA(type.backgroundColor, 0.5));
          gradient.addColorStop(1, this.hexToRgbA(type.backgroundColor, 0.25));

          return {
            label: type.name,
            borderColor: type.backgroundColor,
            pointBackgroundColor: type.backgroundColor,
            borderWidth: 0,
            pointBorderColor: type.backgroundColor,
            backgroundColor: gradient,
            fill: false,
            pointRadius: 1,
            data
          };
        });

      this.datacollection = {
        labels: dates,
        datasets
      };
    },
    getRandomInt() {
      return Math.floor(Math.random() * (50 - 5 + 1)) + 5;
    },
    formattedDate(date, formatting = "iiii, do MMMM 'at' HH:mm") {
      if (!date) {
        return false;
      }
      return format(new Date(date), formatting);
    }
  }
};
</script>
